import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { Nullable, TeamUserLicence, TeamUserRole } from "@shaarp/common";
import { useCallback } from "react";

type AccessReturn = {
  hasAccess: ({
    requiredRoles,
    requiredLicences,
    licenceActiveState,
  }: {
    requiredRoles?: TeamUserRole[];
    requiredLicences?: TeamUserLicence[];
    licenceActiveState?: boolean;
  }) => boolean;
  isTeamPaymentFailed: boolean;
  isTeamSubscriptionDeleted: boolean;
  isTeamNoActiveSubscription: boolean;
};

export default function useAccess(): AccessReturn {
  const { teamSelected } = useTeams();
  const { isAdminMode } = useAdminMode();

  const hasAccess = (
    userRole: TeamUserRole | undefined,
    userLicence: Nullable<TeamUserLicence> | undefined,
    requiredRoles: TeamUserRole[],
    requiredLicences: TeamUserLicence[],
    licenceActiveState: boolean
  ): boolean => {
    // force all access for admin
    if (userRole === TeamUserRolesEnum.ADMIN && isAdminMode) {
      return true;
    }

    const isTeamCreationSetupIncomplete =
      teamSelected?.setupTeamCreation === true;
    const isTeamTrialEndedSetupIncomplete =
      teamSelected?.trialEndsAt &&
      new Date() > new Date(teamSelected.trialEndsAt);
    const isTeamDisabled = teamSelected?.status === "disabled";
    const isTeamArchived = teamSelected?.status === "archived";
    const isTeamBillingError = !!teamSelected?.billingError;
    const isTeamBlockedByUnpaidInvoice =
      teamSelected?.blockedByUnpaidInvoice === true;

    // The manager's baseline seat is unpaid: billing is on, a payment method is
    // required but none is on file. Active trials keep access — enforcement only
    // kicks in once there is no active trial.
    const isTrialActive =
      !!teamSelected?.trialEndsAt &&
      new Date() < new Date(teamSelected.trialEndsAt);
    const isPaymentMethodMissing =
      teamSelected?.paymentMethodMissed === true && !isTrialActive;

    const userHasLicenceActive =
      teamSelected !== null &&
      !isTeamCreationSetupIncomplete &&
      !isTeamTrialEndedSetupIncomplete &&
      !isTeamDisabled &&
      !isTeamArchived &&
      !isTeamBillingError &&
      !isTeamBlockedByUnpaidInvoice &&
      !isPaymentMethodMissing;

    const checkLicenceActive = licenceActiveState === userHasLicenceActive;
    if (!checkLicenceActive) {
      return false;
    }

    const hasRequiredRoles = requiredRoles && requiredRoles.length > 0;
    const hasRequiredLicences = requiredLicences && requiredLicences.length > 0;

    // If neither of the two lists is filled, access is granted by default
    if (!hasRequiredRoles && !hasRequiredLicences) {
      return true;
    }

    // If only one of the two lists is filled (OR logic)
    if (hasRequiredRoles && !hasRequiredLicences && userRole) {
      return requiredRoles.some((role) => userRole.includes(role));
    }

    if (!hasRequiredRoles && hasRequiredLicences && userLicence) {
      return requiredLicences.some((licence) => userLicence.includes(licence));
    }

    // If both lists are filled (AND logic)
    if (hasRequiredRoles && hasRequiredLicences && userRole && userLicence) {
      const roleMatches = requiredRoles.some((role) => userRole.includes(role));
      const licenceMatches = requiredLicences.some((licence) =>
        userLicence.includes(licence)
      );
      return roleMatches && licenceMatches;
    }

    return false;
  };

  const isTeamPaymentFailed = teamSelected?.billingError === 'payment_failed';
  const isTeamSubscriptionDeleted = teamSelected?.billingError === 'subscription_deleted';
  const isTeamNoActiveSubscription = teamSelected?.billingError === 'no_active_subscription';

  return {
    hasAccess: useCallback(
      ({
        requiredRoles = [],
        requiredLicences = [],
        licenceActiveState = true,
      }: {
        requiredRoles?: TeamUserRole[];
        requiredLicences?: TeamUserLicence[];
        licenceActiveState?: boolean;
      }) =>
        hasAccess(
          teamSelected?.userRole,
          teamSelected?.userLicence,
          requiredRoles,
          requiredLicences,
          licenceActiveState
        ),
      [teamSelected]
    ),
    isTeamPaymentFailed,
    isTeamSubscriptionDeleted,
    isTeamNoActiveSubscription,
  };
}
